import { useRef, useState } from "react";
import { useConfirm } from "../../../components/Dialog";
import { IconDownload } from "../../../components/Icons";
import { useAuth } from "../../../context/AuthContext";

type Props = {
  leadId: number;
  leadUlid: string;
  reportUrl: string | null;
  reportName: string | null;
  onAfterChange: () => void;
};

type ActionState = null | "generating" | "downloading" | "uploading";

const BASE_URL =
  process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") ??
  "http://localhost:8000";

export default function ReportActions({
  leadId,
  leadUlid,
  reportUrl,
  reportName,
  onAfterChange,
}: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const uploadInput = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState<ActionState>(null);
  const [feedback, setFeedback] = useState<
    | { kind: "success" | "error"; message: string }
    | null
  >(null);

  const reportSuccess = (msg: string) => setFeedback({ kind: "success", message: msg });
  const reportError = (msg: string) => setFeedback({ kind: "error", message: msg });

  const headers = (extra: Record<string, string> = {}): Record<string, string> => ({
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  });

  const handleGenerate = async () => {
    if (busy) return;
    const ok = await confirm({
      title: reportUrl ? "Regenerar reporte" : "Generar reporte",
      message: reportUrl
        ? "Esto regenerará el CSV del lead con los datos actuales y sobrescribirá el existente. ¿Continuar?"
        : "Se generará un CSV con los datos actuales del lead. ¿Continuar?",
      confirmLabel: reportUrl ? "Regenerar" : "Generar",
      tone: reportUrl ? "warning" : "primary",
    });
    if (!ok) return;
    setBusy("generating");
    setFeedback(null);
    try {
      const response = await fetch(
        `${BASE_URL}/api/admin/operations/leads/${leadUlid}/report/generate`,
        { method: "POST", headers: headers() },
      );
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(`HTTP ${response.status} ${detail.slice(0, 120)}`);
      }
      reportSuccess(reportUrl ? "Report regenerado" : "Report generado");
      onAfterChange();
    } catch (err) {
      reportError(err instanceof Error ? err.message : "Generate falló");
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async () => {
    if (busy || !reportUrl) return;
    setBusy("downloading");
    setFeedback(null);
    try {
      const response = await fetch(reportUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = reportName ?? `Report-${leadId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      reportSuccess("CSV descargado");
    } catch (err) {
      reportError(err instanceof Error ? err.message : "Descarga falló");
    } finally {
      setBusy(null);
    }
  };

  const handleUploadFile = async (file: File | undefined) => {
    if (!file || busy) return;
    const ok = await confirm({
      title: "Reemplazar CSV",
      message: "Esto reemplazará el CSV del lead con el archivo subido. ¿Continuar?",
      confirmLabel: "Reemplazar",
      tone: "danger",
    });
    if (!ok) return;
    setBusy("uploading");
    setFeedback(null);
    try {
      const body = new FormData();
      body.append("file", file);
      const response = await fetch(
        `${BASE_URL}/api/admin/operations/leads/${leadUlid}/report/upload`,
        { method: "POST", headers: headers(), body },
      );
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(`HTTP ${response.status} ${detail.slice(0, 120)}`);
      }
      reportSuccess("CSV reemplazado");
      onAfterChange();
    } catch (err) {
      reportError(err instanceof Error ? err.message : "Upload falló");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: "white",
        padding: "0.75rem 0.9rem",
        borderRadius: "0.55rem",
        borderLeft: "3px solid #0069d9",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        marginBottom: "1rem",
      }}
    >
      <h4
        style={{
          margin: "0 0 0.55rem",
          fontSize: ".7rem",
          textTransform: "uppercase",
          fontWeight: 600,
          color: "#0069d9",
          letterSpacing: "0.5px",
        }}
      >
        Acciones de reporte
      </h4>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
        <ActionButton
          onClick={handleGenerate}
          disabled={!!busy}
          loading={busy === "generating"}
          title={
            reportUrl
              ? "Regenera el CSV con los datos actuales y sobrescribe el existente"
              : "Genera un CSV con los datos actuales del lead"
          }
          tone="primary"
        >
          {reportUrl ? "Regenerar" : "Generar"}
        </ActionButton>

        <ActionButton
          onClick={handleDownload}
          disabled={!!busy || !reportUrl}
          loading={busy === "downloading"}
          title={
            reportUrl
              ? "Descarga el CSV almacenado en el bucket"
              : "No hay reporte para descargar"
          }
          tone="neutral"
        >
          <IconDownload style={{ width: ".95rem", height: ".95rem" }} />
          <span>Descargar</span>
        </ActionButton>

        <ActionButton
          onClick={() => uploadInput.current?.click()}
          disabled={!!busy}
          loading={busy === "uploading"}
          title="Subir un CSV personalizado que reemplaza el reporte del lead"
          tone="warning"
        >
          Subir
        </ActionButton>
        <input
          ref={uploadInput}
          type="file"
          hidden
          accept="text/csv,.csv"
          onChange={(e) => {
            handleUploadFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>

      {feedback && (
        <div
          style={{
            marginTop: "0.55rem",
            padding: "0.45rem 0.65rem",
            borderRadius: "0.4rem",
            fontSize: ".78rem",
            background: feedback.kind === "success" ? "#d4edda" : "#fde2e2",
            color: feedback.kind === "success" ? "#155724" : "#b32424",
          }}
        >
          {feedback.message}
        </div>
      )}
    </div>
  );
}

function ActionButton({
  onClick,
  disabled,
  loading,
  title,
  tone,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  title: string;
  tone: "primary" | "neutral" | "warning";
  children: React.ReactNode;
}) {
  const palette = TONE_PALETTE[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.4rem",
        padding: "0.45rem 0.85rem",
        borderRadius: "0.45rem",
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        color: palette.fg,
        fontSize: ".8rem",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled && !loading ? 0.6 : 1,
      }}
    >
      {loading ? "…" : children}
    </button>
  );
}

const TONE_PALETTE = {
  primary: { bg: "#0069d9", fg: "white", border: "#0069d9" },
  neutral: { bg: "white", fg: "#1f2933", border: "#dee2e6" },
  warning: { bg: "#fff8e1", fg: "#856404", border: "#ffe69c" },
} as const;
